/**
 * PlusTV / 首页配置 GET /banner
 * - 轮播：data.setting.user_center.banner
 * - 热门 Skills：data.setting.user_center.skill
 */

import api from '@/utils/api'

const SKILL_GRADIENTS = [
  'from-amber-800/90 to-orange-950',
  'from-violet-800 to-purple-950',
  'from-emerald-800 to-teal-950',
  'from-rose-800 to-fuchsia-950',
  'from-slate-600 to-slate-900',
  'from-cyan-800 to-blue-950'
]

function assertApiOk(res) {
  if (res == null || typeof res !== 'object') return
  const c = res.code
  if (c === undefined || c === null) return
  const n = Number(c)
  if (n === 0 || n === 200) return
  const msg = res.message || res.msg || res.error || `接口 code=${c}`
  throw new Error(msg)
}

function getUserCenter(res) {
  if (res == null || typeof res !== 'object') return null
  const d = res.data
  if (!d || typeof d !== 'object') return null
  const setting = d.setting
  if (!setting || typeof setting !== 'object') return null
  const userCenter = setting.user_center
  if (!userCenter || typeof userCenter !== 'object') return null
  return userCenter
}

/**
 * @param {object} res
 * @returns {object[]}
 */
export function unwrapUserCenterBanners(res) {
  const userCenter = getUserCenter(res)
  if (!userCenter) return []
  const banner = userCenter.banner
  return Array.isArray(banner) ? banner : []
}

/**
 * @param {object} res
 * @returns {object[]}
 */
export function unwrapUserCenterSkills(res) {
  const userCenter = getUserCenter(res)
  if (!userCenter) return []
  const skill = userCenter.skill
  if (Array.isArray(skill)) return skill
  if (skill && typeof skill === 'object') return [skill]
  return []
}

function resolveBannerLink(link) {
  if (!link || typeof link !== 'object') return ''
  const type = link.type != null ? String(link.type) : ''
  const url = link.url != null ? String(link.url).trim() : ''
  if (type === 'url' && url) return url
  if (url) return url
  return ''
}

/**
 * @param {object} item
 * @param {number} index
 */
export function mapBannerItem(item, index = 0) {
  const text = item.text != null && item.text !== '' ? String(item.text) : ''
  const name = item.name != null && item.name !== '' ? String(item.name) : text
  const linkUrl = resolveBannerLink(item.link)
  return {
    id: item.id != null ? String(item.id) : `banner-${index}`,
    name,
    title: text || name || '',
    desc: '',
    thumb: item.image || item.thumb || item.cover || '',
    videoUrl: item.video_url || item.video || '',
    linkUrl,
    linkType: item.link && item.link.type != null ? String(item.link.type) : '',
    ctaText: text ? `查看${text}` : ''
  }
}

/**
 * @param {object} item
 * @param {number} index
 */
export function mapSkillItem(item, index = 0) {
  const title = item.text != null ? String(item.text) : item.title != null ? String(item.title) : ''
  const desc =
    item.futext != null
      ? String(item.futext)
      : item.desc != null
        ? String(item.desc)
        : item.subtitle != null
          ? String(item.subtitle)
          : ''
  const image = item.image || item.thumb || item.icon || ''
  const prompt = String(
    item.prompt ||
      item.prompt_hint ||
      item.futext ||
      item.desc ||
      item.subtitle ||
      ''
  ).trim()
  return {
    id: item.id != null ? String(item.id) : item.uuid != null ? String(item.uuid) : `skill-${index}`,
    title,
    desc,
    prompt,
    systemPrompt: String(item.system_prompt || item.systemPrompt || '').trim(),
    modelId: String(item.model_id || item.modelId || '').trim(),
    modelKind: String(item.model_kind || item.modelKind || '').trim(),
    image: image ? String(image) : '',
    videoUrl: String(item.videourl || item.video_url || item.video || '').trim(),
    linkUrl: resolveBannerLink(item.link) || String(item.link_url || '').trim(),
    category: String(item.category || item.type || item.tag || item.group || '').trim(),
    thumbGradient: SKILL_GRADIENTS[index % SKILL_GRADIENTS.length]
  }
}

/**
 * 优先 GET /plus_skills（后台配置），失败再回退 /banner user_center.skill
 * @returns {Promise<{skills: object[], source: string}>}
 */
export async function fetchSkillsCatalog(opts = {}) {
  const category = opts.category != null ? String(opts.category) : ''
  try {
    const params = { per_page: opts.perPage || 48, page: opts.page || 1 }
    if (category && category !== 'all') params.category = category
    const res = await api.get('/plus_skills', { params })
    assertApiOk(res)
    const body = res && res.data != null ? res.data : res || {}
    const inner =
      body && body.data && typeof body.data === 'object' && !Array.isArray(body.data)
        ? body.data
        : body
    const list = Array.isArray(inner.list)
      ? inner.list
      : Array.isArray(body.list)
        ? body.list
        : Array.isArray(res && res.list)
          ? res.list
          : []
    if (list.length) {
      const skills = list
        .filter((item) => item && (item.text || item.title || item.image || item.prompt))
        .map((item, i) => mapSkillItem(item, i))
      if (skills.length) return { skills, source: 'plus_skills' }
    }
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[plus_skills]', e)
    }
  }
  try {
    const { skills } = await fetchUserCenterConfig()
    return { skills: Array.isArray(skills) ? skills : [], source: 'banner' }
  } catch (e) {
    return { skills: [], source: 'none' }
  }
}

/** 一次请求拉取 banner + skill */
export async function fetchUserCenterConfig() {
  const res = await api.get('/banner')
  assertApiOk(res)
  const banners = unwrapUserCenterBanners(res)
    .filter((item) => item && (item.image || item.thumb || item.cover))
    .map((item, i) => mapBannerItem(item, i))
  const skills = unwrapUserCenterSkills(res)
    .filter((item) => item && (item.text || item.title || item.image))
    .map((item, i) => mapSkillItem(item, i))
  return { banners, skills }
}

/** @deprecated 使用 fetchUserCenterConfig */
export async function fetchBannerList() {
  const { banners } = await fetchUserCenterConfig()
  return banners
}
